import { Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {
  NgbDateAdapter,
  NgbDatepickerI18n,
  NgbDateStruct,
  NgbInputDatepicker,
  NgbTimeStruct
} from '@ng-bootstrap/ng-bootstrap';
import { NgbDatePickerI18nAdapter, NgbEpochDateStringAdapter } from '../../utils/ngb-datepicker-utils';
import { IoniconsOutline } from '@pt/pt-models';

@Component({
  selector: 'pt-input-time-date-picker',
  template: `
    <form [formGroup]="dateFormGroup" class="w-100">
      <div class="row w-100 m-0">
        <pt-input-label
          style="display: contents"
          *ngIf="label"
          [label]="label"
          [inputId]="inputId"
          [fontWeightBold]="fontWeightBold"
          [isRequired]="isRequired"
        ></pt-input-label>

        <div class="input-group input-group-sm w-100 p-0" style="height: 25px">
          <pt-icon
            *ngIf="
              withClearButton && dateFormGroup.controls.dateTimeToShow.value
            "
            (click)="clearValue()"
            class="clear-icon"
            [ngStyle]="{ right: liteVersion ? '5px' : '45px' }"
            [size]="13"
            [key]="IconClose"
          ></pt-icon>

          <input
            id="date-input"
            #b
            formControlName="dateTimeToShow"
            class="form-control form-control-sm"
            [ngStyle]="{
              width: inputWidth + 'px',
              'border-top-left-radius': roundedBorder ? '5px' : 'initial',
              'border-bottom-left-radius': roundedBorder ? '5px' : 'initial',
              'border-radius': roundedBorder ? '5px' : 'initial'
            }"
            [readOnly]="readOnlyInput"
            [placeholder]="localizedPlaceholder"
            [class.is-invalid]="invalidDate || missingRequiredDate"
            (click)="liteVersion ? onCalendarIcon(d) : null"
          />

          <input
            class="form-control form-control-sm border-0"
            style="width: 0;min-width: 0;padding: 0; max-width: 0;"
            name="dp"
            ngbDatepicker
            #d="ngbDatepicker"
            formControlName="date"
            [minDate]="minDate"
            [container]="bodyContainer ? 'body' : null"
            [positionTarget]="bodyContainer ? b : null"
            [footerTemplate]="
              !hideTimePicker && !liteVersion ? timePickerTemplate : null
            "
          />

          <div
            *ngIf="!liteVersion"
            class="input-group-text p-0"
            style="height: inherit"
          >
            <button
              type="button"
              class="btn text-muted"
              [disabled]="readOnlyInput || isDisabled"
              (click)="onCalendarIcon(d)"
            >
              <pt-icon [key]="IconCalendar" [size]="18"></pt-icon>
            </button>
          </div>
        </div>
        <div style="height: 20px" *ngIf="invalidDate">
          <span class="small text-danger">Invalid date</span>
        </div>
      </div>
    </form>

    <ng-template #timePickerTemplate>
      <div
        class="w-100 d-flex flex-row justify-content-center"
        [formGroup]="dateFormGroup"
      >
        <ngb-timepicker formControlName="time"></ngb-timepicker>
      </div>
    </ng-template>
  `,
  styles: [
    `
      #date-input:read-only {
        background-color: #e9ecef;
      }

      .clear-icon {
        position: absolute;
        top: 4px;
        z-index: 10;
        color: #656565;
        cursor: pointer;
      }

      .clear-icon:hover {
        color: black;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputTimeDatePickerComponent),
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputTimeDatePickerComponent),
      multi: true,
    },
    { provide: NgbDateAdapter, useClass: NgbEpochDateStringAdapter },
    { provide: NgbDatepickerI18n, useClass: NgbDatePickerI18nAdapter },
  ],
})
export class InputTimeDatePickerComponent
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor
{
  @Input() controlName: string;
  @Input() label: string;
  @Input() hideTimePicker = false;
  @Input() lang: string;
  @Input() fontWeightBold: boolean;
  @Input() bodyContainer = false;
  @Input() readOnlyInput = false;
  @Input() roundedBorder = false;
  @Input() withClearButton = false;
  @Input() inputWidth = 150;
  @Input() minDate?: NgbDateStruct;
  @Input() liteVersion = false; //without btn, ag-grid filter options
  @Input() isRequired = false;
  public isDisabled = false;
  public dateFormGroup: FormGroup;
  public invalidDate = false;
  public missingRequiredDate = false;
  public inputId: string;
  public IconCalendar = IoniconsOutline.calendarCheck;
  public IconClose = IoniconsOutline.close;

  public isChanged = false;
  public localizedPlaceholder: string;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  private onChanged: (val) => void;
  private onTouched: () => void;
  private dateDDMMYYYYRegex = new RegExp(/^((\d{2})\/(\d{2})\/(\d{4}))$/);
  private dateMMDDYYYYRegex = this.dateDDMMYYYYRegex;
  private dateYYYYMMDDRegex = new RegExp(/^((\d{4})\/(\d{2})\/(\d{2}))$/);
  private dateDDMMYYYYHHMMRegex = new RegExp(
    /^((\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}))$/
  );
  private dateMMDDYYYYHHMMRegex = this.dateDDMMYYYYHHMMRegex;
  private dateYYYYMMDDHHMMRegex = new RegExp(
    /^((\d{4})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2}))$/
  );

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.isRequired) {
      this.dateFormGroup?.markAsTouched();
      // this.missingRequiredDate = changes.isRequired.currentValue;
    }
  }

  ngOnInit(): void {
    this.inputId = 'ptWInputText_' + this.controlName;

    switch (true) {
      case this.isDDMMYYYYLocalization(this.lang):
        this.localizedPlaceholder = this.hideTimePicker
          ? 'dd/MM/yyyy'
          : 'dd/MM/yyyy HH:mm';
        break;

      case this.isYYYYMMDDLocalization(this.lang):
        this.localizedPlaceholder = this.hideTimePicker
          ? 'yyyy/MM/dd'
          : 'yyyy/MM/dd HH:mm';
        break;

      case this.isMMDDYYYYLocalization(this.lang):
        this.localizedPlaceholder = this.hideTimePicker
          ? 'MM/dd/yyyy'
          : 'MM/dd/yyyy HH:mm';
        break;

      default:
        this.localizedPlaceholder = this.hideTimePicker
          ? 'dd/MM/yyyy'
          : 'dd/MM/yyyy HH:mm';
        break;
    }

    this.dateFormGroup = new FormGroup({
      date: new FormControl(null, [Validators.required]),
      time: new FormControl(null),
      dateTimeToShow: new FormControl(),
    });

    this.dateFormGroup.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((t) => {
        this.isChanged = t.value !== null;
        if (this.onTouched) {
          this.onTouched();
        }
      });

    this.dateFormGroup.controls.date.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((t) => {
        const time = this.dateFormGroup.controls.time.value;
        const d = new Date(t);

        if (time) {
          d.setHours(time.hour);
          d.setMinutes(time.minute);
        }

        this.setDateTimeToShow(
          d,
          {
            second: d.getSeconds(),
            hour: d.getHours(),
            minute: d.getMinutes(),
          },
          this.lang
        );
      });

    this.dateFormGroup.controls.time.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        filter((time) => !!time)
      )
      .subscribe((t) => {
        const time: NgbTimeStruct = t;
        const currentDate = new Date(this.dateFormGroup.controls.date.value);
        currentDate.setHours(time.hour);
        currentDate.setMinutes(time.minute);
        this.dateFormGroup.controls.date.setValue(currentDate);
      });

    this.dateFormGroup.controls.dateTimeToShow.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((t) => {
        this.notifyFromDateToShow(t, this.lang);
      });
  }

  isValidMinDate(timestemp: number, minDate?: NgbDateStruct): boolean {
    if (minDate && timestemp) {
      const min = new Date();
      min.setFullYear(minDate.year, minDate.month - 1, minDate.day);
      return min.getTime() <= timestemp;
    }
    return true;
  }

  writeValue(date: number): void {
    if (date !== null && date !== undefined && Number.isSafeInteger(date)) {
      const parsedDate: string = this.convertEpochDate(date, this.lang);
      this.dateFormGroup.controls.date.setValue(date, { emitEvent: false });
      this.dateFormGroup.controls.dateTimeToShow.setValue(parsedDate, {
        emitEvent: false,
      });
      const groups = this.createLocalizedGroupDate(
        this.dateFormGroup.controls.dateTimeToShow.value,
        this.lang
      )?.map((element) => Number(element));
      if (!this.hideTimePicker && groups) {
        this.dateFormGroup.controls.time.setValue(
          {
            hour: Number(groups[5]),
            minute: Number(groups[6]),
            second: 0,
          },
          { emitEvent: false }
        );
      }
      this.dateFormGroup.updateValueAndValidity();
      this.dateFormGroup.markAsUntouched();
      this.missingRequiredDate = false;
    } else {
      this.dateFormGroup.controls.date.setValue(null, { emitEvent: false });
      this.dateFormGroup.controls.dateTimeToShow.setValue(null, {
        emitEvent: false,
      });
      this.dateFormGroup.controls.time.setValue(null, { emitEvent: false });

      this.dateFormGroup.markAsUntouched();
      this.missingRequiredDate = this.isRequired;
    }
  }

  setDateTimeToShow(d: Date, time: NgbTimeStruct, lang: string) {
    const parsedDate = this.toDateToShow(d, time, lang);
    this.dateFormGroup.controls.dateTimeToShow.setValue(parsedDate);
  }

  toDateToShow(d: Date, time: NgbTimeStruct, lang: string) {
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    const hours = `${time.hour}`.padStart(2, '0');
    const minutes = `${time.minute}`.padStart(2, '0');
    return this.splitDateToString(lang, day, month, year, hours, minutes);
  }

  onCalendarIcon($event: NgbInputDatepicker) {
    if (!this.isDisabled) {
      if (!$event.isOpen()) {
        let date: Date = null;
        if (this.dateFormGroup.controls.dateTimeToShow.value) {
          const groups = this.createLocalizedGroupDate(
            this.dateFormGroup.controls.dateTimeToShow.value,
            this.lang
          )?.map((element) => Number(element));

          if (groups) {
            switch (true) {
              case this.isDDMMYYYYLocalization(this.lang):
                date = new Date(groups[4], Number(groups[3] - 1), groups[2]);
                break;
              case this.isYYYYMMDDLocalization(this.lang):
                date = new Date(groups[2], Number(groups[3] - 1), groups[4]);
                break;
              case this.isMMDDYYYYLocalization(this.lang):
                date = new Date(groups[4], Number(groups[2] - 1), groups[3]);
                break;
              default:
                date = new Date(groups[4], Number(groups[3] - 1), groups[2]);
                break;
            }
            if (!this.hideTimePicker) {
              this.dateFormGroup.controls.date.setValue(date);
              if (groups) {
                this.dateFormGroup.controls.time.setValue(
                  {
                    hour: Number(groups[5]),
                    minute: Number(groups[6]),
                    second: 0,
                  },
                  { emitEvent: false }
                );
              }
            } else {
              const now = new Date(Date.now());
              date.setHours(now.getHours());
              date.setMinutes(now.getMinutes());
              date.setSeconds(0);
              this.dateFormGroup.controls.date.setValue(date);
            }
          } else {
            this.dateFormGroup.controls.date.setValue(new Date(Date.now()));
          }
        }
      }
      $event.toggle();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  convertEpochDate(epochDate: number, lang: string): string {
    if (epochDate) {
      const date = new Date(epochDate);
      const day = date.getDate() < 10 ? '0' + date.getDate() : date.getDate();
      const month =
        date.getMonth() + 1 < 10
          ? '0' + (date.getMonth() + 1)
          : date.getMonth() + 1;
      const year =
        date.getFullYear() < 10 ? '0' + date.getFullYear() : date.getFullYear();
      const hours =
        date.getHours() < 10 ? '0' + date.getHours() : date.getHours();
      const minutes =
        date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes();
      return this.splitDateToString(lang, day, month, year, hours, minutes);
    } else {
      return null;
    }
  }

  clearValue() {
    this.dateFormGroup.controls.dateTimeToShow.setValue(null);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    switch (true) {
      case this.invalidDate:
        return { invalidDate: true };
      case this.missingRequiredDate:
        return { missingRequiredDate: true };
      default:
        return null;
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.dateFormGroup.controls.dateTimeToShow.disable({ emitEvent: false });
    } else {
      this.dateFormGroup.controls.dateTimeToShow.enable({ emitEvent: false });
    }
    this.validate(this.dateFormGroup);
  }

  private notifyFromDateToShow(date: string, lang: string) {
    this.invalidDate = false;
    this.missingRequiredDate = false;

    if (this.isDateValid(date, lang)) {
      const groups = this.createLocalizedGroupDate(date, lang);
      let parsedDate: string;

      switch (true) {
        case this.isDDMMYYYYLocalization(this.lang):
          parsedDate = this.groupDateToDDMMYYYYHHMMString(groups);
          break;

        case this.isYYYYMMDDLocalization(this.lang):
          parsedDate = this.groupDateToYYYYMMDDHHMMString(groups);
          break;

        case this.isMMDDYYYYLocalization(this.lang):
          parsedDate = this.groupDateToMMDDYYYYHHMMString(groups);
          break;

        default:
          parsedDate = this.groupDateToDDMMYYYYHHMMString(groups);
          break;
      }
      const epochParsedDate = new Date(parsedDate).getTime();
      if (this.onTouched) {
        if (Number.isNaN(epochParsedDate)) {
          this.invalidDate = true;
          if (this.onChanged) {
            this.onChanged(date?.length > 0 ? date : null);
          }
        } else {
          if (this.isValidMinDate(epochParsedDate, this.minDate)) {
            if (this.onChanged) this.onChanged(date?.length > 0 ? date : null);
          } else {
            this.invalidDate = true;
            if (this.onChanged) {
              this.onChanged(date?.length > 0 ? date : null);
            }
          }
        }
      }
    } else {
      if (this.isRequired) {
        this.missingRequiredDate = true;
      }
      if (!date) {
        if (this.onChanged) this.onChanged(null);
      } else {
        this.invalidDate = true;
        if (this.onChanged) {
          this.onChanged(date?.length > 0 ? date : null);
        }
      }
    }
  }

  private isDDMMYYYYLocalization(lang: string) {
    switch (lang) {
      case 'it':
      case 'it-CH':
      case 'en-GB':
      case 'fr':
      case 'fr-BE':
        return true;
      default:
        return false;
    }
  }

  private isYYYYMMDDLocalization(lang: string) {
    switch (lang) {
      case 'fr-CA':
      case 'zh':
        return true;
      default:
        return false;
    }
  }

  private isMMDDYYYYLocalization(lang: string) {
    switch (lang) {
      case 'en':
      case 'en-US':
      case 'es-US':
        return true;
      default:
        return false;
    }
  }

  private groupDateToDDMMYYYYHHMMString(groups: RegExpMatchArray) {
    return this.hideTimePicker
      ? `${groups[4]}-${groups[3]}-${groups[2]}`
      : `${groups[4]}-${groups[3]}-${groups[2]}T${groups[5]}:${groups[6]}:00`;
  }

  private groupDateToYYYYMMDDHHMMString(groups: RegExpMatchArray) {
    return this.hideTimePicker
      ? `${groups[2]}-${groups[3]}-${groups[4]}`
      : `${groups[2]}-${groups[3]}-${groups[4]}T${groups[5]}:${groups[6]}:00`;
  }

  private groupDateToMMDDYYYYHHMMString(groups: RegExpMatchArray) {
    return this.hideTimePicker
      ? `${groups[4]}-${groups[2]}-${groups[3]}`
      : `${groups[4]}-${groups[2]}-${groups[3]}T${groups[5]}:${groups[6]}:00`;
  }

  private splitDateToDDMMYYYYHHMMString(
    year: number | string,
    month: number | string,
    day: number | string,
    hours: number | string,
    minutes: number | string
  ) {
    return this.hideTimePicker
      ? `${day}/${month}/${year}`
      : `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  private splitDateToYYYYMMDDHHMMString(
    year: number | string,
    month: number | string,
    day: number | string,
    hours: number | string,
    minutes: number | string
  ) {
    return this.hideTimePicker
      ? `${year}/${month}/${day}`
      : `${year}/${month}/${day} ${hours}:${minutes}`;
  }

  private splitDateToMMDDYYYYHHMMString(
    year: number | string,
    month: number | string,
    day: number | string,
    hours: number | string,
    minutes: number | string
  ) {
    return this.hideTimePicker
      ? `${month}/${day}/${year}`
      : `${month}/${day}/${year} ${hours}:${minutes}`;
  }

  private splitDateToString(
    lang: string,
    day: string | number,
    month: string | number,
    year: string | number,
    hours: string | number,
    minutes: string | number
  ) {
    switch (true) {
      case this.isDDMMYYYYLocalization(lang):
        return this.splitDateToDDMMYYYYHHMMString(
          year,
          month,
          day,
          hours,
          minutes
        );

      case this.isYYYYMMDDLocalization(lang):
        return this.splitDateToYYYYMMDDHHMMString(
          year,
          month,
          day,
          hours,
          minutes
        );

      case this.isMMDDYYYYLocalization(lang):
        return this.splitDateToMMDDYYYYHHMMString(
          year,
          month,
          day,
          hours,
          minutes
        );

      default:
        return this.splitDateToDDMMYYYYHHMMString(
          year,
          month,
          day,
          hours,
          minutes
        );
    }
  }

  private isDateValid(date: string, lang: string) {
    if (date) {
      if (this.liteVersion || this.hideTimePicker) {
        switch (true) {
          case this.isDDMMYYYYLocalization(lang):
            return this.dateDDMMYYYYRegex.test(date);
          case this.isYYYYMMDDLocalization(lang):
            return this.dateYYYYMMDDRegex.test(date);
          case this.isMMDDYYYYLocalization(lang):
            return this.dateMMDDYYYYRegex.test(date);
          default:
            return this.dateDDMMYYYYRegex.test(date);
        }
      } else {
        switch (true) {
          case this.isDDMMYYYYLocalization(lang):
            return this.dateDDMMYYYYHHMMRegex.test(date);
          case this.isYYYYMMDDLocalization(lang):
            return this.dateYYYYMMDDHHMMRegex.test(date);
          case this.isMMDDYYYYLocalization(lang):
            return this.dateMMDDYYYYHHMMRegex.test(date);
          default:
            return this.dateDDMMYYYYHHMMRegex.test(date);
        }
      }
    } else {
      return false;
    }
  }

  private createLocalizedGroupDate(date: string, lang: string) {
    // Qui mi viene data null
    if (date) {
      if (this.liteVersion || this.hideTimePicker) {
        switch (true) {
          case this.isDDMMYYYYLocalization(lang):
            return date.match(this.dateDDMMYYYYRegex);
          case this.isYYYYMMDDLocalization(lang):
            return date.match(this.dateYYYYMMDDRegex);
          case this.isMMDDYYYYLocalization(lang):
            return date.match(this.dateMMDDYYYYRegex);
          default:
            return date.match(this.dateDDMMYYYYRegex);
        }
      } else {
        switch (true) {
          case this.isDDMMYYYYLocalization(lang):
            return date.match(this.dateDDMMYYYYHHMMRegex);
          case this.isYYYYMMDDLocalization(lang):
            return date.match(this.dateYYYYMMDDHHMMRegex);
          case this.isMMDDYYYYLocalization(lang):
            return date.match(this.dateMMDDYYYYHHMMRegex);
          default:
            return date.match(this.dateDDMMYYYYHHMMRegex);
        }
      }
    }
    return null;
  }
}
